import { Box, Stack, Typography } from "@mui/material";
import { GetWasteContainerQuery } from "generated-graphql/graphql";

import {
  containerFillFraction,
  countAddsAndRemoves,
  formatAmountCapacity,
} from "./containerDisplay";
import { SectionCard } from "./containerSections";

type WasteContainerDetail = NonNullable<GetWasteContainerQuery["wasteContainer"]>;

/**
 * The "fill" gauge from the Waste Specs detail page: a vertical track that
 * fills from the bottom to how full the container is, beside the amount /
 * capacity and a tally of adds vs removes. The track stays empty when the
 * fraction can't be computed (mixed units, no capacity) — the numbers still
 * tell the story.
 */
export function ContainerFillCard({ container }: { container: WasteContainerDetail }) {
  const fraction = containerFillFraction(container);
  const { adds, removes } = countAddsAndRemoves(container.fillEvents);
  const addLabel = adds === 1 ? "add" : "adds";
  const removeLabel = removes === 1 ? "remove" : "removes";

  return (
    <SectionCard title="Fill">
      <Stack direction="row" spacing={4} alignItems="center">
        <Box
          sx={{
            position: "relative",
            width: 64,
            height: 120,
            borderRadius: 2,
            bgcolor: "divider",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {fraction !== null && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: `${fraction * 100}%`,
                bgcolor: "rgba(15, 95, 107, 0.5)",
                borderTop: fraction > 0 ? "1px solid #fff" : "none",
                // Ease the level to its new height when a fill event changes
                // the total, mirroring the dashboard bar charts. Held to the
                // motion-safe path so it's inert under reduced-motion.
                "@media (prefers-reduced-motion: no-preference)": {
                  transition: "height 0.3s ease-in-out",
                },
              }}
            />
          )}
        </Box>
        <Stack spacing={1}>
          <Typography variant="body1">{formatAmountCapacity(container)}</Typography>
          <Typography variant="body2" color="text.secondary">
            {adds} {addLabel} &bull; {removes} {removeLabel}
          </Typography>
        </Stack>
      </Stack>
    </SectionCard>
  );
}
